/**
 * Cart Store
 * Zustand store for managing cart state
 * Uses service layer for business logic
 */

import { create } from 'zustand';
import {
  CartItemInput,
  CartItem,
  Cart,
  ValidationError,
  TransferResult,
} from '../services/cart/cartTypes';
import {
  isAuthenticated,
  addItemToAnonymousCart,
  removeItemFromAnonymousCart,
  clearAnonymousCart,
  getAnonymousCartCount,
  getAnonymousCartTotal,
} from '../services/cart/anonymousCartService';
import { isGuestCheckoutActive } from '../services/checkout/guestCheckoutSession';
import {
  fetchCart as fetchCartFromAPI,
  addItemToCart as addItemToCartAPI,
  removeItemFromCart as removeItemFromCartAPI,
  clearCart as clearCartAPI,
  transferAnonymousCart as transferAnonymousCartAPI,
  fetchAnonymousCartDetails,
} from '../services/cart/cartService';

/**
 * Build a Cart from the anonymous sessionStorage items by resolving listing
 * details via the public listing endpoint. Returns null when empty.
 */
const resolveAnonymousCart = async (): Promise<Cart | null> => {
  const items = await fetchAnonymousCartDetails();
  if (items.length === 0) {
    return null;
  }
  return { items, holdExpiresAt: null };
};

// Re-export types for backward compatibility
export type { CartItemInput, CartItem, ValidationError };

/**
 * Whether the cart should be read/written through the backend instead of the
 * sessionStorage anonymous cart. True for logged-in users, and also true for
 * a guest once they submit checkout contact info — at that point their items
 * live in a server-side `guest:<uuid>` cart (see cartIdentity.ts) so they get
 * a real hold and server-authoritative pricing, exactly like a logged-in
 * user's cart.
 */
const usesServerCart = (): boolean => isAuthenticated() || isGuestCheckoutActive();

interface CartState {
  cart: Cart | null;
  validationErrors: ValidationError[];
  isLoading: boolean;
  fetchCart: () => Promise<void>;
  addItem: (_item: CartItemInput) => Promise<void>;
  removeItem: (_index: number) => Promise<void>;
  clearCart: () => Promise<void>;
  transferAnonymousCart: () => Promise<TransferResult>;
  getItemCount: () => number;
  getTotal: () => number;
}

export const useCartStore = create<CartState>((set, get) => ({
  cart: null,
  validationErrors: [],
  isLoading: false,

  fetchCart: async () => {
    // Anonymous browsing: resolve listing details from sessionStorage items
    if (!usesServerCart()) {
      set({ isLoading: true });
      try {
        const cart = await resolveAnonymousCart();
        set({
          cart,
          validationErrors: [],
          isLoading: false,
        });
      } catch {
        set({ isLoading: false, cart: null, validationErrors: [] });
      }
      return;
    }

    // Server-cart flow: logged-in users and guests mid-checkout
    set({ isLoading: true });
    try {
      const data = await fetchCartFromAPI();
      set({
        cart: data.cart,
        validationErrors: data.validationErrors || [],
        isLoading: false,
      });
    } catch {
      set({ isLoading: false, cart: null, validationErrors: [] });
    }
  },

  addItem: async (item) => {
    // Anonymous user flow
    if (!usesServerCart()) {
      try {
        addItemToAnonymousCart(item);
        const cart = await resolveAnonymousCart();
        set({
          cart,
          validationErrors: [],
        });
      } catch (error) {
        console.error('Failed to add item to anonymous cart:', error);
        throw error;
      }
      return;
    }

    // Authenticated user flow
    try {
      const data = await addItemToCartAPI(item);
      set({
        cart: data.cart,
        validationErrors: data.validationErrors || [],
      });
    } catch (error) {
      console.error('Failed to add item to cart:', error);
      throw error;
    }
  },

  removeItem: async (index) => {
    // Anonymous user flow
    if (!usesServerCart()) {
      try {
        removeItemFromAnonymousCart(index);
        const cart = await resolveAnonymousCart();
        set({
          cart,
          validationErrors: [],
        });
      } catch (error) {
        console.error('Failed to remove item from anonymous cart:', error);
        throw error;
      }
      return;
    }

    // Authenticated user flow
    try {
      const data = await removeItemFromCartAPI(index);
      set({
        cart: data.cart,
        validationErrors: data.validationErrors || [],
      });
    } catch (error) {
      console.error('Failed to remove item from cart:', error);
      throw error;
    }
  },

  clearCart: async () => {
    // Anonymous user flow
    if (!usesServerCart()) {
      try {
        clearAnonymousCart();
        set({ cart: null, validationErrors: [] });
      } catch (error) {
        console.error('Failed to clear anonymous cart:', error);
        throw error;
      }
      return;
    }

    // Authenticated user flow
    try {
      await clearCartAPI();
      set({ cart: null, validationErrors: [] });
    } catch (error) {
      console.error('Failed to clear cart:', error);
      throw error;
    }
  },

  transferAnonymousCart: async () => {
    const result = await transferAnonymousCartAPI();

    // Fetch the updated cart from backend after transfer
    if (result.transferred > 0) {
      try {
        const data = await fetchCartFromAPI();
        set({
          cart: data.cart,
          validationErrors: data.validationErrors || [],
        });
      } catch (error) {
        console.error('Failed to fetch cart after transfer:', error);
      }
    }

    return result;
  },

  getItemCount: () => {
    // For anonymous users, count from sessionStorage
    if (!usesServerCart()) {
      return getAnonymousCartCount();
    }
    // For authenticated users, count from cart state
    return get().cart?.items.length || 0;
  },

  getTotal: () => {
    // For anonymous users, calculate from sessionStorage
    if (!usesServerCart()) {
      return getAnonymousCartTotal();
    }
    // For authenticated users, calculate from cart state
    const items = get().cart?.items || [];
    return items.reduce((sum, item) => sum + item.price, 0);
  },
}));
